
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, ThumbsUp, MessageCircle, Database } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface FeedbackItem {
  id: string;
  rating: number;
  feedback: string;
  created_at: string; // Updated to match Supabase column name
  user_id?: string;   // Updated to match Supabase column name
}

const FeedbackStats: React.FC = () => {
  const { language } = useLanguage();
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState<'indexedDB' | 'localStorage' | 'supabase' | 'sample'>('localStorage');

  // Load feedback data whenever sheet is opened
  useEffect(() => {
    if (isOpen) {
      loadFeedbackData();
    }
  }, [isOpen]);

  // Initial load of feedback data
  useEffect(() => {
    loadFeedbackData();
    
    // Listen for online/offline events
    const handleOnline = () => {
      console.log('Device is online, reloading feedback data');
      loadFeedbackData();
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Listen for feedback updates
  useEffect(() => {
    // Create a custom event listener for feedback updates
    const handleFeedbackUpdate = () => {
      console.log("Feedback update event received");
      loadFeedbackData();
    };
    
    // Listen for feedback synced events
    const handleFeedbackSynced = () => {
      console.log("Feedback sync event received");
      loadFeedbackData();
    };
    
    window.addEventListener('feedbackUpdated', handleFeedbackUpdate);
    window.addEventListener('feedbackSynced', handleFeedbackSynced);
    
    return () => {
      window.removeEventListener('feedbackUpdated', handleFeedbackUpdate);
      window.removeEventListener('feedbackSynced', handleFeedbackSynced);
    };
  }, []);

  // Get feedback from IndexedDB
  const getFeedbackFromIndexedDB = (): Promise<FeedbackItem[]> => {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }
      
      const request = indexedDB.open('adaaltFeedback', 1);
      
      request.onsuccess = (event) => {
        // Fix: Use as IDBOpenDBRequest to safely access result
        const db = (event.target as IDBOpenDBRequest).result;
        try {
          const transaction = db.transaction(['feedback'], 'readonly');
          const store = transaction.objectStore('feedback');
          const getAllRequest = store.getAll();
          
          getAllRequest.onsuccess = () => {
            resolve(getAllRequest.result);
          };
          
          getAllRequest.onerror = (error) => {
            console.error('Error getting feedback from IndexedDB:', error);
            reject(error);
          };
        } catch (error) {
          console.error('Error creating transaction:', error);
          reject(error);
        }
      };
      
      request.onerror = (error) => {
        console.error('Error opening IndexedDB:', error);
        reject(error);
      };
    });
  };

  // Try to get feedback from Supabase
  const getFeedbackFromSupabase = async (): Promise<FeedbackItem[]> => {
    try {
      // Fix: Use the correct feedback table that we created
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting feedback from Supabase:', error);
      return [];
    }
  };

  // Load feedback data with fallbacks
  const loadFeedbackData = async () => {
    setIsLoading(true);
    let feedbackData: FeedbackItem[] = [];
    
    try {
      // Try IndexedDB first for complete data
      try {
        feedbackData = await getFeedbackFromIndexedDB();
        if (feedbackData.length > 0) {
          console.log('Loaded feedback from IndexedDB:', feedbackData.length, 'items');
          setDataSource('indexedDB');
        }
      } catch (error) {
        console.log('Could not load from IndexedDB, trying localStorage');
      }
      
      // If no data from IndexedDB, try localStorage
      if (feedbackData.length === 0) {
        try {
          const storedFeedback = JSON.parse(localStorage.getItem('adaaltFeedback') || '[]');
          if (storedFeedback.length > 0) {
            // Convert to the correct format if needed
            feedbackData = storedFeedback.map((item: any) => ({
              id: item.id,
              rating: item.rating,
              feedback: item.feedback,
              created_at: item.created_at || item.createdAt, // Handle both formats
              user_id: item.user_id || item.user
            }));
            console.log('Loaded feedback from localStorage:', feedbackData.length, 'items');
            setDataSource('localStorage');
          }
        } catch (error) {
          console.error('Error loading from localStorage:', error);
        }
      }
      
      // If online and still no data, try Supabase
      if (navigator.onLine && feedbackData.length === 0) {
        try {
          const supabaseFeedback = await getFeedbackFromSupabase();
          if (supabaseFeedback.length > 0) {
            feedbackData = supabaseFeedback;
            console.log('Loaded feedback from Supabase:', feedbackData.length, 'items');
            setDataSource('supabase');
            
            // Update localStorage with this data
            localStorage.setItem('adaaltFeedback', JSON.stringify(feedbackData));
            
            // Also update IndexedDB
            updateIndexedDBWithFeedback(feedbackData);
          }
        } catch (error) {
          console.error('Error loading from Supabase:', error);
        }
      }
      
      // Set the feedback data
      if (feedbackData.length > 0) {
        setFeedbackItems(feedbackData);
        
        const sum = feedbackData.reduce((acc, item) => acc + item.rating, 0);
        setAverageRating(parseFloat((sum / feedbackData.length).toFixed(1)));
        setTotalFeedback(feedbackData.length);
      } else {
        // If no real feedback, create sample feedback
        createSampleFeedback();
      }
      
    } catch (error) {
      console.error('Error loading feedback stats:', error);
      toast.error(language === 'ur' ? 'آراء لوڈ کرنے میں خرابی' : 'Error loading reviews');
      // Still create sample data if there was an error
      createSampleFeedback();
    } finally {
      setIsLoading(false);
    }
  };

  // Update IndexedDB with feedback data
  const updateIndexedDBWithFeedback = (feedbackData: FeedbackItem[]) => {
    if (!('indexedDB' in window)) return;
    
    const request = indexedDB.open('adaaltFeedback', 1);
    
    request.onsuccess = (event) => {
      // Fix: Use as IDBOpenDBRequest to safely access result
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = db.transaction(['feedback'], 'readwrite');
      const store = transaction.objectStore('feedback');
      
      // Clear existing data
      store.clear().onsuccess = () => {
        // Add all feedback items
        feedbackData.forEach(item => {
          store.add(item);
        });
      };
    };
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  // For demo purposes, create some sample feedback if none exists
  const createSampleFeedback = () => {
    if (feedbackItems.length === 0) {
      const sampleFeedback: FeedbackItem[] = [
        {
          id: '1',
          rating: 5,
          feedback: language === 'ur' 
            ? 'بہت اچھی ایپلیکیشن ہے! دستاویزات آسانی سے بنانے میں مدد کرتی ہے۔'
            : 'Great app! It helps me create documents easily.',
          created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: '2',
          rating: 4,
          feedback: language === 'ur'
            ? 'اچھی ایپلیکیشن ہے مگر کچھ اور سہولیات شامل کریں۔'
            : 'Good application but could use some more features.',
          created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
        }
      ];
      setFeedbackItems(sampleFeedback);
      setAverageRating(4.5);
      setTotalFeedback(2);
      setDataSource('sample');
    }
  };

  // Get data source label
  const getDataSourceLabel = () => {
    switch (dataSource) {
      case 'indexedDB': return language === 'ur' ? 'مقامی ڈیٹا بیس' : 'Local Database';
      case 'localStorage': return language === 'ur' ? 'مقامی اسٹوریج' : 'Local Storage';
      case 'supabase': return 'Supabase';
      case 'sample': return language === 'ur' ? 'نمونہ ڈیٹا' : 'Sample Data';
      default: return '';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2 border-blue-200 hover:bg-blue-50"
          onClick={() => setIsOpen(true)}
        >
          <ThumbsUp size={16} className="text-primary" />
          <div className="flex flex-col items-start text-xs">
            <span className="font-medium">{averageRating}/5</span>
            <span className="text-gray-500">
              {totalFeedback} {language === 'ur' ? 'رائے' : 'reviews'}
            </span>
          </div>
        </Button>
      </SheetTrigger>
      <SheetContent 
        className={`${language === 'ur' ? 'rtl' : 'ltr'} w-full sm:max-w-md`}
        side={language === 'ur' ? 'left' : 'right'}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageCircle size={18} />
            {language === 'ur' ? 'صارفین کی آراء' : 'User Feedback'}
          </SheetTitle>
        </SheetHeader>
        
        <div className="py-4">
          <div className="flex items-center justify-center gap-2 mb-6 mt-2">
            <span className="text-4xl font-bold">{averageRating}</span>
            <div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <Star
                    key={value}
                    size={16}
                    className={
                      value <= Math.round(averageRating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {totalFeedback} {language === 'ur' ? 'رائے' : 'reviews'}
              </span>
            </div>
          </div>

          {/* Data source indicator */}
          <div className="flex items-center justify-center text-xs text-gray-500 mb-4">
            <Database size={12} className="mr-1" />
            {getDataSourceLabel()}
          </div>

          <div className="space-y-4 mt-6 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-2 text-gray-500">
                  {language === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                </p>
              </div>
            ) : feedbackItems.length > 0 ? (
              feedbackItems.map((item) => (
                <Card key={item.id} className="shadow-sm">
                  <CardContent className="py-4">
                    <div className="flex justify-between items-start">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <Star
                            key={value}
                            size={14}
                            className={
                              value <= item.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm break-words">{item.feedback}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                {language === 'ur' ? 'ابھی تک کوئی آراء نہیں' : 'No reviews yet'}
              </div>
            )}
          </div>
          
          {/* Reload button */}
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => loadFeedbackData()}
              disabled={isLoading}
              className="flex items-center gap-1.5"
            >
              {isLoading ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
              ) : null}
              {language === 'ur' ? 'تازہ کریں' : 'Refresh'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FeedbackStats;
